import { Box, Typography } from "@mui/material"
import type { BiothreatSignal } from "@phc/common/gen/threatAnalyticsGen"
import type React from "react"
import { useMemo } from "react"
import { extraColors } from "../../../utils/theme"
import type { PrecomputedTab } from "../types"
import { diseaseRollup } from "./biothreatV2Signal"
import {
  V2_ACCENT,
  V2_BODY_FONT_SIZE,
  V2_EYEBROW,
  V2_NUMERIC,
  V2_SEVERITY_COLOR,
  V2_SURFACE,
} from "./biothreatV2Tokens"

export const BiothreatV2TopDiseases: React.FC<{
  rows: BiothreatSignal[]
  kind: PrecomputedTab
  onSelect: (row: BiothreatSignal) => void
}> = ({ rows, kind, onSelect }) => {
  const rollup = useMemo(() => diseaseRollup(rows), [rows])

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${V2_SURFACE.hairline}`,
        borderRadius: "8px",
        backgroundColor: V2_SURFACE.panel,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          px: 1.75,
          py: 1.25,
          borderBottom: `1px solid ${V2_SURFACE.hairline}`,
        }}
      >
        <Typography sx={{ ...V2_EYEBROW, color: extraColors.white }}>
          Top diseases
        </Typography>
        <Typography sx={{ ...V2_EYEBROW, color: extraColors.translucent50 }}>
          {rollup.length} tracked
        </Typography>
      </Box>
      <Box sx={{ overflowY: "auto", flex: 1 }}>
        {rollup.length === 0 ? (
          <Typography
            sx={{ ...V2_EYEBROW, color: extraColors.translucent50, p: 2 }}
          >
            No signals match current filters
          </Typography>
        ) : (
          rollup.map(group => {
            // GDELT tints rows by severity; NWSS has no severity scale, so it
            // falls back to the ambient accent.
            const tint =
              kind === "gdelt" ? V2_SEVERITY_COLOR[group.tier] : V2_ACCENT.cyan
            return (
              <Box
                key={group.disease}
                component="button"
                onClick={() => onSelect(group.lead)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  width: "100%",
                  textAlign: "left",
                  cursor: "pointer",
                  background: "transparent",
                  border: "none",
                  borderBottom: `1px solid ${V2_SURFACE.hairline}`,
                  px: 1.75,
                  py: 1.25,
                  "&:hover": { backgroundColor: extraColors.translucent5 },
                }}
              >
                <Box
                  sx={{
                    width: 4,
                    alignSelf: "stretch",
                    borderRadius: 2,
                    backgroundColor: tint,
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: V2_BODY_FONT_SIZE,
                      fontWeight: 700,
                      color: extraColors.white,
                      textTransform: "capitalize",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {group.diseaseLabel}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: extraColors.translucent50,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {group.locations.join(" · ")}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <Typography
                    sx={{
                      ...V2_NUMERIC,
                      fontSize: "1rem",
                      fontWeight: 800,
                      color: tint,
                      lineHeight: 1,
                    }}
                  >
                    {group.count}
                  </Typography>
                  <Typography
                    sx={{ ...V2_EYEBROW, color: extraColors.translucent50 }}
                  >
                    {group.count === 1 ? "Cluster" : "Clusters"}
                  </Typography>
                </Box>
              </Box>
            )
          })
        )}
      </Box>
    </Box>
  )
}
